import { useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';
import { trackEvent } from '../utils/analytics';

export function useMeetupInfo(isHost, squadDetails) {
  const [meetupPoint, setMeetupPoint] = useState('');
  const [meetupTime, setMeetupTime] = useState('');
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Update meetup form state when user becomes captain
  useEffect(() => {
    if (isHost && squadDetails) {
      setMeetupPoint(squadDetails.meetup_point || '');
      // Format datetime-local input from ISO string
      if (squadDetails.meetup_time) {
        const date = new Date(squadDetails.meetup_time);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        setMeetupTime(`${year}-${month}-${day}T${hours}:${minutes}`);
      } else {
        setMeetupTime('');
      }
      setNotes(squadDetails.notes || '');
    }
  }, [isHost, squadDetails]);

  const saveMeetupInfo = async (squadId, attendeeUuid, refreshSquadDetails) => {
    if (!isHost || !squadDetails || !attendeeUuid || !supabase) return;

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Convert datetime-local format to ISO string
      const meetupTimeISO = meetupTime ? new Date(meetupTime).toISOString() : null;

      // Update meetup info using Supabase RPC or direct update
      const { error } = await supabase.rpc('update_squad_meetup', {
        p_squad_id: squadDetails.squad_id,
        p_attendee_uuid: attendeeUuid,
        p_meetup_point: meetupPoint.trim() || null,
        p_meetup_time: meetupTimeISO,
        p_notes: notes.trim() || null,
      });

      if (error) {
        throw new Error(error.message || 'Failed to save meet-up info');
      }

      trackEvent('Meetup Info Saved', {
        squad_id: squadDetails.squad_id,
        has_meetup_point: !!meetupPoint.trim(),
        has_meetup_time: !!meetupTime,
        has_notes: !!notes.trim(),
      });

      setSaveMessage({ type: 'success', text: 'Saved' });
      
      // Refresh squad details to show updated info
      await refreshSquadDetails();

      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving meet-up info:', error);
      setSaveMessage({ 
        type: 'error', 
        text: error instanceof Error ? error.message : 'Failed to save. Please try again.' 
      });
      trackEvent('Meetup Info Save - Error', {
        squad_id: squadDetails.squad_id,
        error_message: error instanceof Error ? error.message : 'Unknown error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    meetupPoint,
    setMeetupPoint,
    meetupTime,
    setMeetupTime,
    notes,
    setNotes,
    isSaving,
    saveMessage,
    saveMeetupInfo,
  };
}
